import { GridApi } from 'ag-grid-community';
import { uniqBy } from 'lodash';

import { SelectableFilterOption }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';
import { ProductData } from '@/pages/UpliftsManager/UpliftTool/components/grid/columns';
import { 
  ContractLineBillingCycleOutput, 
  ContractBillingStatusPascalCaseEnum,
  VariableContractLineOutput 
} from '@/graphql/contracts/_generated_/contracts';
import { ContractStatusFormatted } from '@/pages/ContractDetails/ContractLines/grid/variableContractLinescolumns';
import { EnvUtils } from '@/utils/env.utils';

const getRowsData = <TData = any>(api: GridApi) => {
  const rowsData: TData[] = [];
  api.forEachNode((node) => rowsData.push(node.data));
  return rowsData;
};

export class ContractLineFilterOptions {
  static getStatusFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLine>(api);
    const options = contractLines.filter(cl => !!cl).map((el) => ({
      text: el.status.formattedStatus,
      returnValue: el.status.status
    }));

    return uniqBy(options, 'returnValue');
  }

  static getStatusBCFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLineBillingCycleOutput>(api);
    const options = contractLines.filter(cl => !!cl).map((el) => ({
      text: ContractStatusFormatted[el.status!],
      returnValue: el.status!,
    }));

    return uniqBy(options, 'returnValue');
  }

  static getVariableStatusFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<VariableContractLineOutput>(api);
    const options = contractLines.filter(cl => !!cl).map((el) => ({
      text: ContractStatusFormatted[el.status!],
      returnValue: el.status!,
    }));

    return uniqBy(options, 'returnValue');
  }

  static getBillingTypeFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLine>(api);
    const options = contractLines.filter(cl => !!cl).map((el) => ({
      text: el.billingType.formattedType,
      returnValue: el.billingType.type
    }));

    return uniqBy(options, 'returnValue');
  }

  static getBillingTypeBCFilterOptions(api: GridApi, billingTypes: BillingType[]): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLineBillingCycleOutput>(api);
    const options = contractLines.filter(cl => !!cl).map((el) => ({
      text: billingTypes.find(x => x.type == el.billingType!)?.formattedType ?? el.billingType!,
      returnValue: el.billingType!.valueOf()
    }));
 
    return uniqBy(options, 'returnValue');
  }

  static getBillingIntervalsOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLine>(api);
    const options = (contractLines.filter((el) => el?.billingInterval)).map((el) => ({
      text: el.billingInterval!.formattedBillingInterval,
      returnValue: el.billingInterval!.billingInterval
    }));

    return uniqBy(options, 'returnValue');
  }

  // eslint-disable-next-line max-len
  static getBillingIntervalsBCOptions(api: GridApi, billingIntervals: Pick<BillingInterval, 'billingInterval' | 'formattedBillingInterval'>[]): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLineBillingCycleOutput>(api);
    const options = (contractLines.filter((el) => el?.billingInterval)).map((el) => ({
      text: billingIntervals
        .find(x => x.billingInterval == el.billingInterval!)?.formattedBillingInterval 
        ?? el.billingInterval!,
      returnValue: el.billingInterval!
    }));

    return uniqBy(options, 'returnValue');
  }

  static getBillingIntervalsVariableOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<VariableContractLineOutput>(api);
    const options = (contractLines.filter((el) => el?.billingInterval)).map((el) => ({
      text: el.billingInterval!,
      returnValue: el.billingInterval!
    }));

    return uniqBy(options, 'returnValue');
  }

  static getUnitPriceFrequenciesOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLine>(api);
    const options = (contractLines.filter((el) => el?.unitPriceFrequency)).map((el) => ({
      text: el.unitPriceFrequency!.formattedFrequency,
      returnValue: el.unitPriceFrequency!.frequency
    }));

    return uniqBy(options, 'returnValue');
  }

  // eslint-disable-next-line max-len
  static getUnitPriceFrequenciesBCOptions(api: GridApi, unitPriceFrequencies: UnitPriceFrequency[]): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLineBillingCycleOutput>(api);
    const options = (contractLines.filter((el) => el?.unitPriceFrequency)).map((el) => ({
      text: unitPriceFrequencies
        .find(x => x.frequency == el.unitPriceFrequency)?.formattedFrequency ?? el.unitPriceFrequency!,
      returnValue: el.unitPriceFrequency!
    }));

    return uniqBy(options, 'returnValue');
  }

  static getStockCodeOptions(stockCodes: ProductData[]): SelectableFilterOption[] {
    const uniqueCodes = uniqBy(stockCodes, 'stockCode');
    return uniqueCodes.map(product => ({
      text: product.stockCode,
      returnValue: product.stockCode
    }));
  }

  static getPauseStatusBCFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<ContractLineBillingCycleOutput>(api);
    const isDisableFutureLinePause = EnvUtils.isDisableFutureLinePauseEnabled();
    const pauseStatusDisplayMap: Record<string, string> = {
      [ContractBillingStatusPascalCaseEnum.On]: 'Off',
      [ContractBillingStatusPascalCaseEnum.Paused]: 'Paused',
      [ContractBillingStatusPascalCaseEnum.FuturePause]: 'Future Pause',
    };
    const options = contractLines
      .filter(cl => !!cl && !!cl.lineBillingStatus)
      .filter(
        cl => !(
          isDisableFutureLinePause
          && cl.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
        )
      )
      .map((el) => ({
        text: pauseStatusDisplayMap[el.lineBillingStatus!] ?? el.lineBillingStatus!,
        returnValue: el.lineBillingStatus!,
      }));

    return uniqBy(options, 'returnValue');
  }

  static getPauseStatusVariableFilterOptions(api: GridApi): SelectableFilterOption[] {
    const contractLines = getRowsData<VariableContractLineOutput>(api);
    const isDisableFutureLinePause = EnvUtils.isDisableFutureLinePauseEnabled();
    const pauseStatusDisplayMap: Record<string, string> = {
      [ContractBillingStatusPascalCaseEnum.On]: 'Off',
      [ContractBillingStatusPascalCaseEnum.Paused]: 'Paused',
      [ContractBillingStatusPascalCaseEnum.FuturePause]: 'Future Pause',
    };
    const options = contractLines
      .filter(cl => !!cl && !!cl.lineBillingStatus)
      .filter(
        cl => !(
          isDisableFutureLinePause
          && cl.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
        )
      )
      .map((el) => ({
        text: pauseStatusDisplayMap[el.lineBillingStatus!] ?? el.lineBillingStatus!,
        returnValue: el.lineBillingStatus!,
      }));

    return uniqBy(options, 'returnValue');
  }

  static getStockNameOptions(stockNames: ProductData[]): SelectableFilterOption[] {
    const uniqueCodes = uniqBy(stockNames, 'stockName');
    return uniqueCodes.map(product => ({
      text: product.stockName,
      returnValue: product.stockName
    }));
  }
}
